<template>
  <div class="pp-tab-surface">
    <div
      v-if="!hasPlan"
      class="empty"
    >
      <p>{{ t('property.v2FloorPlans.empty') }}</p>
    </div>
    <template v-else>
      <div
        v-for="(plan, planIdx) in normalizedPlans"
        :key="plan.id ?? planIdx"
        class="pp-plan-block"
        :class="{ 'pp-plan-block--spaced': planIdx > 0 }"
      >
        <!-- Plan header -->
        <div class="pp-desc">
          <div class="eyebrow">
            {{ t('property.v2FloorPlans.eyebrow') }}
            <template v-if="normalizedPlans.length > 1"> · {{ planIdx + 1 }} / {{ normalizedPlans.length }}</template>
          </div>
          <div class="pp-desc__title-row">
            <h3>{{ plan.name }}</h3>
            <span
              v-if="plan.postHandover === false"
              class="badge badge--no-ph"
              >{{ t('property.priceTab.noPostHandover') }}</span
            >
            <span
              v-else-if="plan.postHandover === true"
              class="badge badge--ph"
              >{{ t('property.priceTab.postHandover') }}</span
            >
          </div>
          <p
            v-if="plan.description"
            class="pp-desc__sub"
          >
            {{ plan.description }}
          </p>
        </div>

        <!-- Items list -->
        <div
          v-if="plan.items.length"
          class="pp-plan"
        >
          <div
            v-for="(item, idx) in plan.items"
            :key="item.id ?? idx"
            class="milestone upcoming"
          >
            <div
              class="num"
              aria-hidden="true"
            >
              <span>{{ idx + 1 }}</span>
            </div>
            <div class="body">
              <div class="t">{{ item.name }}</div>
              <div
                v-if="item.description"
                class="d"
              >
                {{ item.description }}
              </div>
            </div>
            <div class="amt">—</div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import type { PropertyDetailed } from '@/types/properties';
import { usePropertyPaymentPlans } from '@/composables/usePropertyPaymentPlans';

const props = defineProps<{
  item: PropertyDetailed;
}>();

const { t } = useI18n();

const normalizedPlans = computed(() => usePropertyPaymentPlans(props.item?.payment_plans));

const hasPlan = computed(() => normalizedPlans.value.length > 0);
</script>

<style scoped lang="scss">
.pp-tab-surface {
  background: #f8f7f3;
  border-radius: 20px;
  padding: 28px;
}

.empty {
  background: white;
  border: 1px solid rgba(13, 15, 21, 0.08);
  border-radius: 14px;
  padding: 40px;
  text-align: center;
  color: rgba(13, 15, 21, 0.62);
  font-size: 14px;
}

.pp-plan-block {
  &--spaced {
    margin-top: 28px;
  }
}

.pp-desc {
  background: white;
  border: 1px solid rgba(13, 15, 21, 0.08);
  border-radius: 16px;
  padding: 24px 26px;
  margin-bottom: 10px;

  .eyebrow {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 10px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: rgba(13, 15, 21, 0.62);
    font-weight: 700;
    margin-bottom: 10px;
  }

  &__title-row {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 8px;

    h3 {
      font-family: 'Fraunces', 'Times New Roman', serif;
      font-weight: 600;
      font-size: 24px;
      letter-spacing: -0.015em;
      margin: 0;
      line-height: 1.15;
    }
  }

  &__sub {
    font-size: 14px;
    line-height: 1.6;
    color: rgba(13, 15, 21, 0.7);
    margin: 0;
  }
}

.badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;

  &--no-ph {
    background: rgba(13, 15, 21, 0.07);
    color: rgba(13, 15, 21, 0.62);
  }

  &--ph {
    background: rgba(255, 184, 0, 0.12);
    color: #9a6e00;
  }
}

.pp-plan {
  display: flex;
  flex-direction: column;
  gap: 10px;

  .milestone {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px 20px;
    background: white;
    border: 1px solid rgba(13, 15, 21, 0.08);
    border-radius: 12px;

    .num {
      width: 44px;
      height: 44px;
      border-radius: 10px;
      background: #0d0f15;
      color: white;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-family: 'Syne', 'Manrope', sans-serif;
      font-weight: 700;
      font-size: 16px;
      flex-shrink: 0;
    }

    &.upcoming .num {
      background: #f8f7f3;
      color: #0d0f15;
      border: 1px solid rgba(13, 15, 21, 0.08);
    }

    .body {
      flex: 1;
      min-width: 0;

      .t {
        font-size: 14px;
        font-weight: 700;
        color: #0d0f15;
      }

      .d {
        font-size: 12.5px;
        color: rgba(13, 15, 21, 0.62);
        margin-top: 1px;
      }
    }

    .amt {
      text-align: end;
      font-family: 'Fraunces', 'Times New Roman', serif;
      font-weight: 600;
      font-size: 20px;
      letter-spacing: -0.015em;
      font-variant-numeric: tabular-nums;
      color: rgba(13, 15, 21, 0.3);
    }
  }
}
</style>
